const BLOG_POSTS = [
  {
    slug: 'clinica-geral',
    title: 'Clínica geral: a base antes de especializar',
    date: '2026-10-30',
    cover: '/blog/covers/blog-esp-01-clinica-geral.png',
    seriesLabel: 'Série Especialidades · Post 1 de 4',
    excerpt:
      'Você acabou de sair da faculdade (ou já atende há anos) e a pergunta bate: preciso especializar agora? Na maioria dos casos, a resposta honesta é: primeiro firme a clínica geral.',
    description:
      'Clínica geral é o chão onde o resto se apoia. O que é na prática, por que começar por aqui, a rotina típica e quando faz sentido especializar.',
    body: `Você acabou de sair da faculdade (ou já atende há anos) e a pergunta bate: *preciso especializar agora?*
Na maioria dos casos, a resposta honesta é: **primeiro firme a clínica geral**. Ela é o chão onde o resto se apoia.

## O que é clínica geral na prática

Clínica geral (ou clínico geral odontológico) é quem resolve o dia a dia do consultório: diagnóstico, prevenção, restaurações, extrações simples, urgências e o acompanhamento do paciente ao longo do tempo. Não é “menos” que uma especialidade — é a visão completa da boca antes de aprofundar em um pedaço dela.

## Por que começar por aqui

1. **Você vê o paciente inteiro.** Ortodontia, implante ou HOF fazem mais sentido quando você já entende o histórico, a oclusão e as limitações daquele caso.
2. **Renda e rotina mais estáveis.** Urgências, manutenção e retorno preenchem a agenda enquanto você decide o próximo passo.
3. **Rede de confiança.** Quem resolve bem o básico vira a pessoa que o paciente indica — e a que outros colegas encaminham de volta.
4. **Menos risco de especializar cedo demais.** Sem volume de casos, a especialidade vira curso caro e agenda vazia.

## Rotina típica de um dia

- Manhã: avaliações, limpezas, restaurações.
- Meio do dia: urgências e encaixes.
- Tarde: retornos, planejamento e encaminhamentos.
- Fim do expediente: anotações no prontuário, confirmações do dia seguinte, materiais.

O que muda o jogo não é “atender mais”, e sim **organizar**: quem vem, com quem, o que já foi feito e o que falta.

## Quando faz sentido especializar

Espere especializar quando você tiver:

- clareza do tipo de caso que mais gosta (e do que evita);
- volume mínimo de pacientes para manter a clínica enquanto estuda;
- tempo e caixa para curso + material + eventual redução de agenda;
- mentoria ou referência local na área escolhida.

Até lá, aprofundar em **protocolos de clínica geral** (anamnese, radiografia, plano de tratamento, documentação) rende mais do que colecionar certificado.

## Primeiros passos se você está começando agora

1. Defina horário de atendimento e bloqueie folga de estudo.
2. Padronize a primeira consulta (queixa, exame, plano, orçamento).
3. Separe o que você encaminha (e para quem).
4. Documente tudo no prontuário — o futuro você (e o paciente) agradecem.
5. Só depois escolha a especialidade da série: ortodontia, implantodontia, HOF etc.

## Como o ÁgilOdonto entra nisso (sem forçar)

Na clínica geral a agenda costuma misturar vários tipos de atendimento e, se houver equipe, mais de um profissional. No [ÁgilOdonto](https://agilodonto.com.br) você organiza a agenda por profissional, guarda a ficha e o histórico no mesmo lugar e usa confirmação por link — o WhatsApp de lembrete continua sendo a recepção quem envia. Teste **20 dias grátis, sem cartão**.`,
  },
  {
    slug: 'ortodontia',
    title: 'Ortodontia: o que é, rotina e primeiros passos',
    date: '2026-10-30',
    cover: '/blog/covers/blog-esp-02-ortodontia.png',
    seriesLabel: 'Série Especialidades · Post 2 de 4',
    excerpt:
      'Depois de firmar a clínica geral, muita gente olha para a ortodontia: aparelho, alinhadores, oclusão e um acompanhamento que dura meses.',
    description:
      'O que é ortodontia, a rotina de avaliação até contenção, o perfil de mercado e os primeiros passos antes de entrar no caminho.',
    body: `Depois de firmar a clínica geral, muita gente olha para a **ortodontia**: aparelho, alinhadores, oclusão e um acompanhamento que dura meses. Antes de entrar nesse caminho, vale entender o dia a dia — não só o curso.

## O que é ortodontia

Ortodontia cuida do alinhamento dos dentes e da relação entre as arcadas. O objetivo não é só “deixar bonito”: é função, estabilidade e saúde periodontal no longo prazo. O tratamento costuma ser longo, com retornos frequentes e documentação fotográfica/radiográfica constante.

## Rotina típica

- Avaliação e documentação (fotos, moldes ou scanner, radiografias).
- Planejamento (aparelho fixo, alinhadores, extracoes eventuais, tempo estimado).
- Instalação e ajustes periódicos (semanas ou meses, conforme o protocolo).
- Contenção e retenção — a fase que muita gente subestima.
- Trabalho em equipe com clínica geral, periodontia e, às vezes, cirurgia.

É uma especialidade de **paciência e processo**: pouco “milagre de uma sessão”, muito acompanhamento.

## Mercado e perfil

Faz sentido quando você gosta de casos longitudinais, conversa bem com adolescente e adulto, e topa investir em material e atualização. Em cidades menores, ortodontia ainda é diferencial; em capitais, a concorrência com alinhadores e redes é maior — o diferencial vira diagnóstico e acompanhamento humano.

## Primeiros passos

1. Tenha base sólida em oclusão e diagnóstico (clínica geral bem feita ajuda).
2. Escolha formação reconhecida (especialização / residência) e veja carga horária prática.
3. Monte fluxo de documentação desde o primeiro caso.
4. Defina o que você encaminha (cirurgia, ATM, casos sindrômicos).
5. Só escale marketing depois que a agenda de retorno estiver controlável.

## ÁgilOdonto no acompanhamento

Ortodontia vive de retorno marcado e histórico claro. No [ÁgilOdonto](https://agilodonto.com.br) a agenda por profissional e o prontuário com evolução ajudam a não perder o fio do tratamento. Teste **20 dias grátis, sem cartão** — lembrete por WhatsApp continua sendo a recepção quem envia.`,
  },
  {
    slug: 'implantodontia',
    title: 'Implantodontia: formação, dia a dia e mercado',
    date: '2026-10-30',
    cover: '/blog/covers/blog-esp-03-implantodontia.png',
    seriesLabel: 'Série Especialidades · Post 3 de 4',
    excerpt:
      'Implante é uma das especialidades que mais aparece na conversa de quem quer “subir o ticket” do consultório. O atrativo é real — mas o dia a dia mistura cirurgia, prótese, planejamento 3D e muita responsabilidade.',
    description:
      'O que é implantodontia, a rotina da triagem à manutenção, o mercado e os primeiros passos com protocolo e documentação.',
    body: `Implante é uma das especialidades que mais aparece na conversa de quem quer “subir o ticket” do consultório. O atrativo é real — mas o dia a dia mistura cirurgia, prótese, planejamento 3D e muita responsabilidade com o paciente.

## O que é implantodontia

É a área que recoloca dentes perdidos com implantes osseointegrados e a prótese sobre eles. Vai de caso unitário simples a reabilitações totais. Exige anatomia, imagem, técnica cirúrgica e visão protética — implantodontista que só “coloca o pino” e some costuma gerar retrabalho.

## Rotina típica

- Triagem e anamnese (saúde sistêmica, ossos, hábitos, expectativa).
- Planejamento com imagem (tomografia, enxerto ou não, carga imediata ou não).
- Cirurgia e pós-operatório.
- Etapa protética e ajustes de oclusão.
- Manutenção: higiene, retorno, complicações (peri-implantite, perda óssea).

É especialidade de **protocolo + comunicação**: o paciente precisa entender tempo, custo e cuidados.

## Mercado

Demanda alta em várias regiões; custo de formação e kit cirúrgico também. Concorrência com cursos curtos é grande — diferencial costuma ser planejamento sério, documentação e parceria com prótese/periodontia. Começar com casos bem indicados vale mais do que volume no começo.

## Primeiros passos

1. Base cirúrgica e protética antes do “curso de fim de semana”.
2. Escolha sistema de implante e aprenda o protocolo de ponta a ponta.
3. Documente foto + imagem + termo de consentimento em todo caso.
4. Tenha rede de apoio (radiologia, prótese, periodontia).
5. Só divulgue resultados com autorização e expectativa realista.

## ÁgilOdonto no fluxo

Implante gera várias etapas e vários profissionais. No [ÁgilOdonto](https://agilodonto.com.br) você separa agenda por profissional, mantém anamnese e mídias (incluindo antes/depois) no prontuário. Teste **20 dias grátis, sem cartão**.`,
  },
  {
    slug: 'hof',
    title: 'HOF / estética facial: caminho depois da graduação',
    date: '2026-10-30',
    cover: '/blog/covers/blog-esp-04-hof.png',
    seriesLabel: 'Série Especialidades · Post 4 de 4',
    excerpt:
      'Harmonização orofacial (HOF) virou caminho popular para quem gosta de estética e quer ampliar o consultório além do “dente”. É área em movimento — formação, legislação e expectativa do paciente mudam rápido.',
    description:
      'O que costuma entrar em HOF, a rotina de avaliação ao retorno, cuidados de formação e os primeiros passos com documentação e consentimento.',
    body: `Harmonização orofacial (HOF) virou caminho popular para quem gosta de estética e quer ampliar o consultório além do “dente”. É área em movimento — formação, legislação e expectativa do paciente mudam rápido. Por isso o post é um mapa, não um manual clínico.

## O que costuma entrar em HOF

Procedimentos que dialogam com o terço inferior e harmonia da face (preenchimentos, toxina, bioestimuladores, fios, entre outros — conforme habilitação e legislação vigentes). O ponto central: **não é “injetar e pronto”**. Anatomia, seleção de paciente, complicações e registro fotográfico fazem parte do pacote.

## Rotina típica

- Avaliação facial e alinhamento de expectativa (o mais importante).
- Planejamento e registro (fotos padronizadas).
- Procedimento e orientações de pós.
- Retorno e retoque, quando indicado.
- Gestão de urgências e efeitos adversos — ter protocolo escrito.

Quem entra em HOF só pelo marketing costuma tropeçar na conversa difícil: “não é o caso” ou “vamos esperar”.

## Formação e cuidado

Prefira cursos com carga prática, tutoria e discussão de complicações. Acompanhe regras do CFO e da sua região. Trabalhe com ficha de anamnese específica, termo esclarecido e fotos autorizadas. Estética sem documentação é risco jurídico e clínico.

## Primeiros passos

1. Domine clínica geral e anatomia de cabeça/pescoço.
2. Escolha formação séria antes de montar “pacote Instagram”.
3. Padronize fotos e consentimento.
4. Defina o que você não faz (e para quem encaminha).
5. Integre HOF à agenda sem atropelar o restante da clínica.

## ÁgilOdonto e o mapa HOF

No [ÁgilOdonto](https://agilodonto.com.br) há espaço para **mapa HOF**, mídias e comparação antes/depois no prontuário — útil para acompanhar evolução e alinhar expectativa. Agenda por profissional ajuda se estética e clínica geral dividem a sala. Teste **20 dias grátis, sem cartão**; WhatsApp de lembrete segue manual pela recepção.`,
  },
];

function listBlogPosts() {
  return BLOG_POSTS;
}

function getBlogPost(slug) {
  return BLOG_POSTS.find((post) => post.slug === slug) || null;
}

function getBlogNeighbors(slug) {
  const index = BLOG_POSTS.findIndex((post) => post.slug === slug);
  if (index < 0) return { prev: null, next: null };
  return {
    prev: index > 0 ? BLOG_POSTS[index - 1] : null,
    next: index < BLOG_POSTS.length - 1 ? BLOG_POSTS[index + 1] : null,
  };
}

module.exports = {
  BLOG_POSTS,
  listBlogPosts,
  getBlogPost,
  getBlogNeighbors,
};
